import { MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { array, bool, mixed, number, object, string } from 'yup';
import { useUpdateDevice } from '~/api/devices';
import { applyServerErrors } from '~/api/helpers';
import { useAssignPropertyGroup, useRemovePropertyGroup } from '~/api/property-groups';
import { ChannelAutocomplete } from '~/components/ChannelAutocomplete';
import { DeviceGroupAutocomplete } from '~/components/DeviceGroupAutocomplete';
import { Tooltip } from '~/components/Tooltip';
import { TvBrandAutocomplete } from '~/components/TvBrandAutocomplete';
import { DetailSelect, DetailSwitch, DetailTextField } from '~/components/forms/details';
import { DurationSlider } from '~/components/inputs';
import type { FormTableData } from '~/components/settings';
import { useAppContext } from '~/contexts';
import { DeviceJpegxsLicense, DeviceNightlyBehavior, PropertyOwnerType } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { duration } from '~/lib/validators';
import { playerSupports } from '~/pages/devices/lib/player-version';
import { withTooltip } from '~/pages/devices/lib/withTooltip';
import type {
  DeviceSettingsPage__Device as Device,
  DeviceSettingsPage__PropertyGroup,
} from '../../queries/settings.generated';
import { AddRemoteTargets } from '../components';

const getFcPropertyGroups = (t: (key: string) => string) => [
  {
    id: -1,
    name: t('pages.devices.show.settings.remote_targets_heading'),
  },
];

const JPEGXS_LICENSE_LABELS: Record<DeviceJpegxsLicense, string> = {
  [DeviceJpegxsLicense.Jpegxs]: 'JPEG XS',
  [DeviceJpegxsLicense.Jpegxs_4K]: 'JPEG XS 4K',
  [DeviceJpegxsLicense.JpegxsF]: 'JPEG XS-F',
  [DeviceJpegxsLicense.JpegxsF_4K]: 'JPEG XS-F 4K',
};

interface PropertyGroupFormData {
  id: number;
  name: string;
  propertyGroupId: number;
  fields: FormTableData[];
}

const validationSchema = object({
  closedCaptioning: bool().required(),
  defaultChannel: object({ id: number().integer().min(1).required() })
    .nullable()
    .defined(),
  demo: bool().required(),
  deviceGroup: object({ id: number().required() }).nullable(),
  edgeServer: bool().required(),
  eventStream: bool().required(),
  internal: bool().required(),
  // Formik converts the empty "no license" option to undefined before validating, which a
  // non-required oneOf accepts.
  jpegxsLicense: mixed<DeviceJpegxsLicense>().oneOf(Object.values(DeviceJpegxsLicense)),
  nightlyBehavior: string().oneOf(['NONE', 'REBOOT', 'RESTART']).required(),
  overscan: number().required().min(50).max(100),
  propertyValuesAttributes: array(object({ id: number().required(), value: string().nullable() })),
  messagingBackend: string().oneOf(['PUBNUB', 'MQTT', 'AWS_IOT']).required(),
  preload: bool().required(),
  realtime: bool().required(),
  remoteTargets: array(number()),
  screenshotInterval: duration({ min: { minutes: 1 }, max: { hours: 1 } }),
  silence: bool().required(),
  tvBrand: object({ id: number().required() }).nullable(),
});

interface UseSettingsFormProps {
  device: Device;
  propertyGroups: readonly DeviceSettingsPage__PropertyGroup[];
}

export const useSettingsForm = ({ device, propertyGroups }: UseSettingsFormProps) => {
  const { t } = useTranslation();
  const [updateDevice] = useUpdateDevice();
  const [assignPropertyGroup] = useAssignPropertyGroup();
  const [removePropertyGroup] = useRemovePropertyGroup();
  const [confirm, confirmDialogProps] = useConfirmDialog();
  const [showAddPropertyDialog, setShowAddPropertyDialog] = useState(false);
  const { currentUser, currentNetwork } = useAppContext();

  const initialValues = useMemo(
    () => ({
      cacheContent: device.cacheContent,
      closedCaptioning: device.closedCaptioning,
      defaultChannel: device.defaultChannel ?? null,
      demo: device.demo,
      deviceGroup: device.group ?? null,
      edgeServer: device.edgeServer,
      eventStream: device.eventStream,
      internal: device.internal,
      jpegxsLicense: device.jpegxsLicense ?? '',
      nightlyBehavior: device.nightlyBehavior,
      overscan: device.overscan || undefined,
      propertyValuesAttributes: device.propertyGroups
        .flatMap((x) => x.propertyValues)
        .map(({ id, propertyDefinition, value }) => ({
          id,
          value: value || propertyDefinition.defaultValue,
        })),
      messagingBackend: device.messagingBackend,
      preload: device.preload,
      realtime: device.realtime,
      remoteTargetIds:
        device.remoteTargets.length > 0 ? device.remoteTargets.map((x) => x.id) : undefined,
      screenshotInterval: device.screenshotInterval ?? '',
      silence: device.silence,
      tvBrand: device.tvBrand ?? null,
    }),
    [device],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setErrors }) => {
      const newValues = validationSchema.cast(values);
      const { defaultChannel, deviceGroup, edgeServer, jpegxsLicense, preload, tvBrand, ...rest } =
        newValues;
      const result = await updateDevice({
        variables: {
          deviceId: device.id,
          patch: {
            ...rest,
            ...(currentUser.admin
              ? { edgeServer, jpegxsLicense: jpegxsLicense || null, preload }
              : {}),
            defaultChannelId: defaultChannel?.id ?? null,
            deviceGroupId: deviceGroup?.id ?? null,
            screenshotInterval: rest.screenshotInterval || null,
            tvBrandId: tvBrand?.id ?? null,
          },
        },
      });
      applyServerErrors(result, setErrors, {
        defaultChannelId: 'defaultChannel',
        deviceGroupId: 'deviceGroup',
        tvBrandId: 'tvBrand',
      });
    },
    validateOnMount: true,
    validationSchema,
  });

  const generalSettings: FormTableData[] = useMemo(() => {
    const nonPubnubBackendsBlocked = !playerSupports(device.playerVersion, 'messagingBackend');
    const preloadBlocked = !playerSupports(device.playerVersion, 'preload');
    // Tooltip needs an inner span with pointer-events: auto so hover fires
    // through the disabled MenuItem's pointer-events: none.
    const renderBackendOption = (label: string) =>
      nonPubnubBackendsBlocked ? (
        <Tooltip
          title={t(
            'pages.devices.show.settings.fields.messaging_backend.version_requirement_tooltip',
          )}
        >
          <span style={{ pointerEvents: 'auto' }}>{label}</span>
        </Tooltip>
      ) : (
        label
      );
    const settings = [
      {
        heading: t('common.labels.group'),
        subHeading: t('pages.devices.show.settings.fields.group.description'),
        dataField: (
          <DeviceGroupAutocomplete
            disabled={formik.isSubmitting || !currentNetwork.canManage.value}
            onChange={(group) => formik.setFieldValue('deviceGroup', group)}
            value={formik.values.deviceGroup}
          />
        ),
      },
      {
        heading: t('pages.devices.show.settings.fields.default_channel.label'),
        subHeading: t('pages.devices.show.settings.fields.default_channel.description'),
        dataField: (
          <ChannelAutocomplete
            disabled={!currentNetwork.canManage.value}
            onChange={(channel) => formik.setFieldValue('defaultChannel', channel)}
            value={formik.values.defaultChannel}
          />
        ),
      },
      {
        heading: t('pages.devices.show.settings.fields.automatic_screenshots.label'),
        subHeading: t('pages.devices.show.settings.fields.automatic_screenshots.description'),
        dataField: (
          <DurationSlider
            max={{ hours: 1 }}
            min={{ minutes: 1 }}
            name="screenshotInterval"
            step={{ minutes: 1 }}
            value={formik.values.screenshotInterval}
            onChange={formik.handleChange}
          />
        ),
      },
      {
        heading: t('pages.devices.show.settings.fields.overscan.label'),
        subHeading: t('pages.devices.show.settings.fields.overscan.description'),
        dataField: (
          <DetailTextField
            disabled={formik.isSubmitting}
            name="overscan"
            value={formik.values.overscan}
            onChange={formik.handleChange}
            type="number"
          />
        ),
      },
      {
        heading: t('pages.devices.show.settings.fields.nightly_reboot.label'),
        subHeading: t('pages.devices.show.settings.fields.nightly_reboot.description'),
        dataField: (
          <DetailSelect
            disabled={
              formik.isSubmitting ||
              !currentNetwork.canManage.value ||
              (!currentUser.admin && formik.values.nightlyBehavior === DeviceNightlyBehavior.None)
            }
            name="nightlyBehavior"
            value={formik.values.nightlyBehavior}
            onChange={formik.handleChange}
          >
            {Object.values(DeviceNightlyBehavior)
              .filter(
                (value) =>
                  currentUser.admin ||
                  value !== DeviceNightlyBehavior.None ||
                  formik.values.nightlyBehavior === DeviceNightlyBehavior.None,
              )
              .map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`pages.devices.show.settings.nightly_behavior.${value.toLowerCase()}`)}
                </MenuItem>
              ))}
          </DetailSelect>
        ),
      },
      {
        heading: t('common.labels.tv_brand'),
        subHeading: t('pages.devices.show.settings.fields.tv_brand.description'),
        dataField: (
          <TvBrandAutocomplete
            disabled={formik.isSubmitting || !currentNetwork.canManage.value}
            onChange={(brand) => formik.setFieldValue('tvBrand', brand)}
            value={formik.values.tvBrand}
          />
        ),
      },
    ];
    if (currentNetwork.canManage.value) {
      settings.push(
        {
          heading: t('pages.devices.show.settings.fields.cache_content.label'),
          subHeading: t('pages.devices.show.settings.fields.cache_content.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.cacheContent}
              color="primary"
              disabled={formik.isSubmitting}
              name="cacheContent"
              onChange={formik.handleChange}
            />
          ),
        },
        {
          heading: t('pages.devices.show.settings.fields.closed_captioning.label'),
          subHeading: t('pages.devices.show.settings.fields.closed_captioning.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.closedCaptioning}
              color="primary"
              disabled={formik.isSubmitting}
              name="closedCaptioning"
              onChange={formik.handleChange}
            />
          ),
        },
        {
          heading: t('pages.devices.show.settings.fields.event_stream.label'),
          subHeading: t('pages.devices.show.settings.fields.event_stream.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.eventStream}
              color="primary"
              disabled={formik.isSubmitting}
              name="eventStream"
              onChange={formik.handleChange}
            />
          ),
        },
        ...(currentUser.admin
          ? [
              {
                heading: t('pages.devices.show.settings.fields.preload.label'),
                subHeading: t('pages.devices.show.settings.fields.preload.description'),
                dataField: withTooltip(
                  <DetailSwitch
                    checked={formik.values.preload}
                    color="primary"
                    disabled={formik.isSubmitting || preloadBlocked}
                    name="preload"
                    onChange={formik.handleChange}
                  />,
                  preloadBlocked
                    ? t('pages.devices.show.settings.fields.preload.version_requirement_tooltip')
                    : undefined,
                ),
              },
            ]
          : []),
        {
          heading: t('pages.devices.show.settings.fields.real_time.label'),
          subHeading: t('pages.devices.show.settings.fields.real_time.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.realtime}
              color="primary"
              disabled={formik.isSubmitting}
              name="realtime"
              onChange={formik.handleChange}
            />
          ),
        },
        ...(formik.values.realtime && currentUser.admin
          ? [
              {
                heading: t('pages.devices.show.settings.fields.messaging_backend.label'),
                subHeading: t('pages.devices.show.settings.fields.messaging_backend.description'),
                dataField: (
                  <DetailSelect
                    disabled={formik.isSubmitting}
                    name="messagingBackend"
                    value={formik.values.messagingBackend}
                    // The gated options are disabled, but their tooltip span
                    // re-enables pointer events so a click can still slip
                    // through. This is the one path every selection routes
                    // through, so reject any blocked backend here.
                    onChange={(event) => {
                      if (nonPubnubBackendsBlocked && event.target.value !== 'PUBNUB') return;
                      formik.handleChange(event);
                    }}
                  >
                    <MenuItem value="PUBNUB">PubNub</MenuItem>
                    {import.meta.env.DEV && (
                      <MenuItem disabled={nonPubnubBackendsBlocked} value="MQTT">
                        {renderBackendOption('MQTT (Dev Only)')}
                      </MenuItem>
                    )}
                    <MenuItem disabled={nonPubnubBackendsBlocked} value="AWS_IOT">
                      {renderBackendOption('IoT Core')}
                    </MenuItem>
                  </DetailSelect>
                ),
              },
            ]
          : []),
        {
          heading: t('pages.devices.show.settings.fields.silence.label'),
          subHeading: t('pages.devices.show.settings.fields.silence.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.silence}
              color="primary"
              disabled={formik.isSubmitting}
              name="silence"
              onChange={formik.handleChange}
            />
          ),
        },
      );
    }

    if (currentUser.admin) {
      settings.push(
        {
          heading: t('pages.devices.show.settings.fields.jpegxs_license.label'),
          subHeading: t('pages.devices.show.settings.fields.jpegxs_license.description'),
          dataField: (
            <DetailSelect
              disabled={formik.isSubmitting}
              displayEmpty
              name="jpegxsLicense"
              value={formik.values.jpegxsLicense}
              onChange={formik.handleChange}
            >
              <MenuItem value="">
                {t('pages.devices.show.settings.fields.jpegxs_license.none')}
              </MenuItem>
              {Object.values(DeviceJpegxsLicense).map((value) => (
                <MenuItem key={value} value={value}>
                  {JPEGXS_LICENSE_LABELS[value]}
                </MenuItem>
              ))}
            </DetailSelect>
          ),
        },
        {
          heading: t('common.labels.internal'),
          subHeading: t('pages.devices.show.settings.fields.internal.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.internal}
              color="primary"
              disabled={formik.isSubmitting || !currentUser.admin}
              name="internal"
              onChange={formik.handleChange}
            />
          ),
        },
        {
          heading: t('common.labels.demo'),
          subHeading: t('pages.devices.show.settings.fields.demo.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.demo}
              color="primary"
              disabled={formik.isSubmitting || !currentUser.admin}
              name="demo"
              onChange={formik.handleChange}
            />
          ),
        },
      );

      if (device.kind === 'LINUX') {
        settings.push({
          heading: t('pages.devices.show.settings.fields.edge_server.label'),
          subHeading: t('pages.devices.show.settings.fields.edge_server.description'),
          dataField: (
            <DetailSwitch
              checked={formik.values.edgeServer}
              color="primary"
              disabled={formik.isSubmitting}
              name="edgeServer"
              onChange={formik.handleChange}
            />
          ),
        });
      }
    }

    return settings;
  }, [currentNetwork.canManage, currentUser.admin, device.kind, device.playerVersion, formik, t]);

  const getPropertyGroupValue = useCallback(
    (id: number) => formik.values.propertyValuesAttributes.find((x) => x.id === id)?.value,
    [formik],
  );

  const setPropertyGroupValue = useCallback(
    (id: number, value: string) => {
      const index = formik.values.propertyValuesAttributes.findIndex((x) => x.id === id);
      void formik.setFieldValue(`propertyValuesAttributes.${index}`, {
        id,
        value: String(value),
      });
    },
    [formik],
  );

  const propertyGroupSettings: PropertyGroupFormData[] = useMemo(
    () =>
      device.propertyGroups.map((pg) => ({
        id: pg.id,
        name: pg.name,
        propertyGroupId: pg.propertyGroupId,
        fields:
          pg.propertyValues.length === 0
            ? [{ heading: t('pages.devices.show.settings.no_settings', { name: pg.name }) }]
            : pg.propertyValues.map(({ id, propertyDefinition }) => ({
                heading: propertyDefinition.label,
                subHeading: propertyDefinition.helpText,
                dataField:
                  propertyDefinition.kind === 'boolean' ? (
                    <DetailSwitch
                      checked={getPropertyGroupValue(id) === 'true'}
                      color="primary"
                      disabled={formik.isSubmitting}
                      onChange={(event) => setPropertyGroupValue(id, String(event.target.checked))}
                    />
                  ) : propertyDefinition.allowedValues != null ? (
                    <DetailSelect
                      onChange={(event) => setPropertyGroupValue(id, String(event.target.value))}
                      value={getPropertyGroupValue(id)}
                    >
                      <MenuItem value="">
                        {t('pages.devices.show.settings.fields.property_none')}
                      </MenuItem>
                      {propertyDefinition.allowedValues.map(({ label, value }, index) => (
                        <MenuItem key={index} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </DetailSelect>
                  ) : (
                    <DetailTextField
                      onChange={(event) => setPropertyGroupValue(id, String(event.target.value))}
                      value={getPropertyGroupValue(id)}
                    />
                  ),
              })),
      })),
    [device, formik, getPropertyGroupValue, setPropertyGroupValue, t],
  );

  const addPropertyGroup = useCallback(
    async ({ id }: { id: string }) => {
      const propertyGroupId = Number(id);

      // handle remote targets situation
      if (propertyGroupId === -1) {
        await formik.setFieldValue('remoteTargetIds', [], true);
        setShowAddPropertyDialog(false);
        return;
      }

      const result = await assignPropertyGroup({
        variables: {
          ownerId: device.id,
          ownerType: PropertyOwnerType.Device,
          propertyGroupId,
        },
      });
      if (!result.errors?.length) {
        setShowAddPropertyDialog(false);
      }
    },
    [assignPropertyGroup, device.id, formik],
  );

  const deletePropertyGroup = useCallback(
    async (propertyGroup: { propertyGroupId: number }) => {
      if (!(await confirm())) return;
      await removePropertyGroup({
        variables: {
          ownerId: device.id,
          ownerType: PropertyOwnerType.Device,
          propertyGroupId: propertyGroup.propertyGroupId,
        },
      });
    },
    [confirm, device, removePropertyGroup],
  );

  const remoteTargetSettings: FormTableData[] = useMemo(
    () => [
      {
        heading: t('pages.devices.show.settings.fields.remote_targets.label'),
        subHeading: t('pages.devices.show.settings.fields.remote_targets.description'),
        dataField: (
          <AddRemoteTargets
            currentDeviceId={device.id}
            onChange={(ids) => formik.setFieldValue('remoteTargetIds', ids, true)}
            remoteTargets={device.remoteTargets}
          />
        ),
      },
    ],
    [device.id, device.remoteTargets, formik, t],
  );

  const filteredPropertyGroups = useMemo(
    () => [
      ...(!formik.values.remoteTargetIds ? getFcPropertyGroups(t) : []),
      ...propertyGroups.filter(
        ({ id }) =>
          !device.propertyGroups.flatMap(({ propertyGroupId }) => propertyGroupId).includes(id),
      ),
    ],
    [device.propertyGroups, formik.values.remoteTargetIds, propertyGroups, t],
  );

  return {
    addPropertyGroup,
    confirmDialogProps,
    deletePropertyGroup,
    filteredPropertyGroups,
    formik,
    generalSettings,
    propertyGroupSettings,
    remoteTargetSettings,
    setShowAddPropertyDialog,
    showAddPropertyDialog,
  };
};
